import { motion } from 'framer-motion';

const getRoomIcon = (room: string) => {
  const normalizedRoom = room.toLowerCase();

  if (normalizedRoom.includes('seal')) return '🦭';
  if (normalizedRoom.includes('orca')) return '🐋';
  if (normalizedRoom.includes('starfish')) return '⭐';
  if (normalizedRoom.includes('dolphin')) return '🐬';
  if (normalizedRoom.includes('practicum')) return '🎓';

  return '📍';
};

interface EducatorCardProps {
  name: string;
  title: string;
  room?: string;
  delay?: number;
  placeholderType?: 'teacher' | 'graduate' | 'coming-soon';
  borderColor?: string;
  particleColor?: string;
  placeholderBgGradient?: string;
  photoSrc?: string;
  bubbleColors?: string[];
}

export default function EducatorCard({
  name,
  title,
  room,
  delay = 0,
  placeholderType = 'teacher',
  borderColor = 'border-yellow-200',
  particleColor = 'bg-yellow-300',
  placeholderBgGradient = 'from-blue-100 to-blue-200',
  photoSrc,
  bubbleColors
}: EducatorCardProps) {

  const getPlaceholderContent = () => {
    switch (placeholderType) {
      case 'teacher':
        return {
          emoji: '👩‍🏫',
          emojiColor: 'text-blue-300',
          label: 'Photo Coming Soon'
        };
      case 'graduate':
        return {
          emoji: '👩‍🎓',
          emojiColor: 'text-purple-300',
          label: 'Photo Coming Soon'
        };
      case 'coming-soon':
        return {
          emoji: '⭐',
          emojiColor: 'text-yellow-500',
          label: 'Photo Coming Soon'
        };
      default:
        return {
          emoji: '👩‍🏫',
          emojiColor: 'text-blue-300',
          label: 'Photo Coming Soon'
        };
    }
  };

  const placeholder = getPlaceholderContent();

  return (
    <motion.div 
      className="text-center relative"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
    >
      {/* PhotoWithBackdrop style effects */}
      <motion.div 
        className={`absolute -top-2 -left-2 w-full h-full border border-dashed ${borderColor} rounded-lg opacity-30`}
        animate={{ 
          rotate: [0, 1, -1, 0],
          scale: [1, 1.01, 1]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          delay: delay * 2
        }}
      />
      
      {/* Animated decorative bubbles (varied sizes, colors, and motion) */}
      {[
        { pos: 'absolute -top-1 -right-1', size: 'w-2 h-2', axis: 'y', amp: 8, dur: 2.0, dmult: 0.5 },
        { pos: 'absolute -bottom-2 -left-2', size: 'w-3 h-3', axis: 'x', amp: 10, dur: 2.4, dmult: 0.8 },
        { pos: 'absolute top-2 -left-1', size: 'w-2 h-2', axis: 'y', amp: 6, dur: 1.8, dmult: 1.1 },
        { pos: 'absolute bottom-3 right-6', size: 'w-4 h-4', axis: 'y', amp: 12, dur: 2.8, dmult: 0.6 },
        { pos: 'absolute -top-3 right-10', size: 'w-2 h-2', axis: 'x', amp: 8, dur: 2.2, dmult: 0.9 }
      ].map((b, i) => (
        <motion.div
          key={i}
          className={`${b.pos} ${b.size} rounded-full ${(bubbleColors && bubbleColors[i % bubbleColors.length]) || (i === 0 ? particleColor : ['bg-amber-300','bg-blue-300','bg-green-300','bg-purple-300'][i - 1])}`}
          animate={
            b.axis === 'y'
              ? { y: [0, -b.amp, 0], opacity: [0.5, 1, 0.5] }
              : { x: [0, b.amp, 0], opacity: [0.5, 1, 0.5] }
          }
          transition={{
            duration: b.dur,
            repeat: Infinity,
            ease: "easeInOut",
            delay: delay * b.dmult
          }}
        />
      ))}

      <div className="relative z-10 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
        {/* Placeholder Image */}
        <div className="w-48 h-48 mx-auto mb-4 rounded-lg relative overflow-hidden">
          {photoSrc ? (
            <motion.img
              src={photoSrc}
              alt={`${name} headshot`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover rounded-lg"
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
            />
          ) : (
            <motion.img
              src="/images/Photo-Coming-Soon-1.png"
              alt="Photo Coming Soon"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover rounded-lg"
              whileHover={{ 
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              animate={{
                opacity: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          )}
        </div>

        {/* Room and Star */}
        <div className="flex justify-center mb-2 h-6 items-center">
          {room ? (
            <>
              <span className="text-lg" aria-hidden="true">{getRoomIcon(room)}</span>
              <span className="text-dblue font-semibold ml-2">{room}</span>
            </>
          ) : null}
        </div>

        {/* Title and Name */}
        <h3 className="text-lg font-bold text-dblue mb-1">{title}</h3>
        <p className="text-gray-600">{name}</p>
      </div>
    </motion.div>
  );
}
